'use client';

import { useState, useCallback } from 'react';
import { parsePhoneNumber, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';
import { phoneMessage } from '@/lib/phoneMessages';

const BR_WHATSAPP_MASK_REGEX = /^\((\d{2})\)\s(9\d{4})-(\d{4})$/;

function formatBrazilianWhatsAppNumber(input: string): string {
  const rawDigits = input.replace(/\D/g, '');
  const digits =
    rawDigits.length > 11 ? rawDigits.slice(-11) : rawDigits.slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length < 3) return `(${digits}`;
  if (digits.length < 8) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export interface PhoneValidationResult {
  isValid: boolean;
  formattedNumber: string;
  internationalNumber: string;
  countryCode: CountryCode | undefined;
  errorMessage: string;
}

export function usePhoneValidation() {
  const [defaultCountry] = useState<CountryCode>('BR');

  const validatePhone = useCallback((phoneNumber: string, country?: CountryCode): PhoneValidationResult => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return {
        isValid: false,
        formattedNumber: '',
        internationalNumber: '',
        countryCode: undefined,
        errorMessage: phoneMessage('required')
      };
    }

    const cleanNumber = phoneNumber.replace(/\D/g, '');
    const countryToUse = country || defaultCountry;

    if (countryToUse === 'BR') {
      const formatted = formatBrazilianWhatsAppNumber(phoneNumber);

      if (cleanNumber.length < 11) {
        return {
          isValid: false,
          formattedNumber: formatted || phoneNumber,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('keepTyping')
        };
      }

      if (cleanNumber.length > 11) {
        return {
          isValid: false,
          formattedNumber: formatted,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('brDigits')
        };
      }

      if (!BR_WHATSAPP_MASK_REGEX.test(phoneNumber.trim())) {
        return {
          isValid: false,
          formattedNumber: formatted,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('brFormat')
        };
      }

      const ddd = Number(cleanNumber.slice(0, 2));
      if (ddd < 11 || ddd > 99) {
        return {
          isValid: false,
          formattedNumber: formatted,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('brDdd')
        };
      }

      if (cleanNumber[2] !== '9') {
        return {
          isValid: false,
          formattedNumber: formatted,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('brFirstDigit')
        };
      }

      return {
        isValid: true,
        formattedNumber: formatted,
        internationalNumber: `+55${cleanNumber}`,
        countryCode: countryToUse,
        errorMessage: ''
      };
    }

    // Se o número está muito incompleto, não mostrar erro ainda
    if (cleanNumber.length < 8) {
      return {
        isValid: false,
        formattedNumber: phoneNumber,
        internationalNumber: '',
        countryCode: countryToUse,
        errorMessage: phoneMessage('keepTyping')
      };
    }

    try {
      // Tenta fazer o parse do número
      const parsed = parsePhoneNumber(phoneNumber, countryToUse);
      
      if (!parsed) {
        return {
          isValid: false,
          formattedNumber: phoneNumber,
          internationalNumber: '',
          countryCode: countryToUse,
          errorMessage: phoneMessage('invalidFormat')
        };
      }

      // Verifica se é um número válido
      const valid = isValidPhoneNumber(phoneNumber, countryToUse);
      
      if (!valid) {
        return {
          isValid: false,
          formattedNumber: parsed.formatNational(),
          internationalNumber: parsed.formatInternational(),
          countryCode: parsed.country,
          errorMessage: phoneMessage('invalid')
        };
      }

      // Verifica se é um número de celular (necessário para WhatsApp)
      const phoneType = parsed.getType();
      if (phoneType && !['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(phoneType)) {
        return {
          isValid: false,
          formattedNumber: parsed.formatNational(),
          internationalNumber: parsed.formatInternational(),
          countryCode: parsed.country,
          errorMessage: phoneMessage('mobileRequired')
        };
      }

      return {
        isValid: true,
        formattedNumber: parsed.formatNational(),
        internationalNumber: parsed.formatInternational(),
        countryCode: parsed.country,
        errorMessage: ''
      };
    } catch {
      return {
        isValid: false,
        formattedNumber: phoneNumber,
        internationalNumber: '',
        countryCode: country || defaultCountry,
        errorMessage: phoneMessage('validationError')
      };
    }
  }, [defaultCountry]);

  const formatPhoneForWhatsApp = useCallback((phoneNumber: string, country?: CountryCode): string => {
    try {
      const countryToUse = country || defaultCountry;
      const parsed = parsePhoneNumber(phoneNumber, countryToUse);
      
      if (!parsed) {
        return '';
      }

      // Retorna no formato que o WhatsApp aceita (apenas números, com código do país)
      // Exemplo: 5511999999999
      return parsed.number.substring(1); // Remove o +
    } catch {
      return '';
    }
  }, [defaultCountry]);

  return {
    validatePhone,
    formatPhoneForWhatsApp,
    defaultCountry
  };
}
